import { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface TweetEmbedProps {
  url: URL;
  className?: string;
}

declare global {
  interface Window {
    twttr?: {
      widgets: {
        createTweet: (
          tweetId: string,
          container: HTMLElement,
          options?: {
            theme?: 'light' | 'dark';
            width?: number;
            align?: 'left' | 'center' | 'right';
            conversation?: 'none' | 'all';
            cards?: 'hidden' | 'visible';
            dnt?: boolean;
          }
        ) => Promise<HTMLElement | undefined>;
        load: (container?: HTMLElement) => void;
      };
      ready: (callback: () => void) => void;
    };
  }
}

function extractTweetId(url: URL): string | null {
  // Handle various Twitter/X URL formats:
  // https://twitter.com/username/status/1234567890
  // https://x.com/username/status/1234567890
  // https://mobile.twitter.com/username/status/1234567890
  // https://www.twitter.com/username/status/1234567890
  
  const pathname = url.pathname;
  const statusMatch = pathname.match(/\/status\/(\d+)/);
  
  if (statusMatch && statusMatch[1]) {
    return statusMatch[1];
  }
  
  return null;
}

function isTwitterUrl(url: URL): boolean {
  const hostname = url.hostname.toLowerCase();
  return hostname === 'twitter.com' || 
         hostname === 'x.com' || 
         hostname === 'www.twitter.com' || 
         hostname === 'www.x.com' || 
         hostname === 'mobile.twitter.com';
}

export function TweetEmbed({ url, className }: TweetEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const tweetId = extractTweetId(url);

  useEffect(() => {
    // Load Twitter widgets script if not already loaded
    if (!window.twttr && !document.querySelector('script[src*="widgets.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://platform.twitter.com/widgets.js';
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      script.onerror = () => {
        setHasError(true);
        setIsLoading(false);
      };
      document.head.appendChild(script);
    } else if (window.twttr) {
      setScriptLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!scriptLoaded || !tweetId || !containerRef.current || !window.twttr) {
      return;
    }

    const container = containerRef.current;
    
    // Clear any existing content
    container.innerHTML = '';

    // Determine theme based on document class
    const isDark = document.documentElement.classList.contains('dark');
    
    window.twttr.ready(() => {
      if (!window.twttr || !container) return;
      
      window.twttr.widgets
        .createTweet(tweetId, container, {
          theme: isDark ? 'dark' : 'light',
          width: 550,
          align: 'center',
          conversation: 'none',
          cards: 'visible',
          dnt: true, // Do not track
        })
        .then((element) => {
          setIsLoading(false);
          if (!element) {
            setHasError(true);
          }
        })
        .catch(() => {
          setIsLoading(false);
          setHasError(true);
        });
    });
  }, [scriptLoaded, tweetId]);

  // If not a Twitter URL or no tweet ID, don't render
  if (!isTwitterUrl(url) || !tweetId) {
    return null;
  }

  if (hasError) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center space-x-3 text-muted-foreground">
            <AlertCircle className="h-5 w-5" />
            <span>Unable to load tweet</span>
            <Button
              variant="outline"
              size="sm"
              asChild
              className="ml-4"
            >
              <a
                href={url.toString()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2"
              >
                <span>View on X</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={className}>
      {isLoading && (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-3/5" />
              </div>
              <Skeleton className="h-48 w-full rounded-lg" />
              <div className="flex space-x-4">
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-16" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <div 
        ref={containerRef} 
        className="flex justify-center"
        style={{ minHeight: isLoading ? '0' : 'auto' }}
      />
    </div>
  );
}